import React from 'react';
import {
    Dialog,
    IconButton,
    Typography,
    Box,
    Button,
    CircularProgress,
    Tooltip,
    Chip,
    Fade
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    RotateRight as RotateIcon,
    FitScreen as FitScreenIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
    TableChart as CsvIcon,
    TextSnippet as TextIcon,
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon,
    Fullscreen as FullscreenIcon
} from '@mui/icons-material';

/* ─────── Text / CSV Preview ─────── */
const TextFilePreview = ({ url, isCsv }) => {
    const [content, setContent] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (!url) return;
        setLoading(true);
        setError(null);
        fetch(url)
            .then(r => { if (!r.ok) throw new Error('Failed to load'); return r.text(); })
            .then(t => { setContent(t); setLoading(false); })
            .catch(e => { setError(e.message); setLoading(false); });
    }, [url]);

    if (loading) return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><CircularProgress sx={{ color: '#818cf8' }} /></Box>;
    if (error) return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#f87171' }}><Typography>Error: {error}</Typography></Box>;

    if (isCsv) {
        const parseCSVLine = (line) => {
            const result = []; let current = ''; let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') { if (inQuotes && line[i + 1] === '"') { current += '"'; i++; } else inQuotes = !inQuotes; }
                else if (char === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
                else current += char;
            }
            result.push(current.trim());
            return result;
        };
        const lines = content.split('\n').filter(l => l.trim());
        const headers = lines[0] ? parseCSVLine(lines[0]) : [];
        const rows = lines.slice(1).map(l => parseCSVLine(l));

        return (
            <Box sx={{ height: '100%', overflow: 'auto', p: 2.5 }}>
                <Box sx={{
                    borderRadius: 3, overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.08)',
                    bgcolor: 'rgba(255,255,255,0.02)',
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3 },
                }}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead>
                                <tr>
                                    <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 700, color: '#6366f1', fontSize: '0.7rem', letterSpacing: 1, textTransform: 'uppercase', borderBottom: '2px solid rgba(99,102,241,0.3)', borderRight: '1px solid rgba(255,255,255,0.06)', background: 'rgba(99,102,241,0.08)', width: 50 }}>#</th>
                                    {headers.map((h, i) => (
                                        <th key={i} style={{
                                            padding: '14px 16px', textAlign: 'left', fontWeight: 700, color: '#c7d2fe',
                                            fontSize: '0.7rem', letterSpacing: 1, textTransform: 'uppercase',
                                            borderBottom: '2px solid rgba(99,102,241,0.3)',
                                            borderRight: i < headers.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                                            background: 'rgba(99,102,241,0.08)', whiteSpace: 'nowrap'
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, ri) => (
                                    <tr key={ri} style={{ transition: 'background 0.15s' }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.06)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <td style={{ padding: '10px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '0.75rem', fontFamily: 'monospace', borderRight: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{ri + 1}</td>
                                        {row.map((cell, ci) => (
                                            <td key={ci} style={{
                                                padding: '10px 16px', color: 'rgba(255,255,255,0.85)',
                                                borderRight: ci < row.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                                                borderBottom: '1px solid rgba(255,255,255,0.04)'
                                            }}>{cell}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>
                <Box sx={{ mt: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Chip label={`${rows.length} rows`} size="small" sx={{ bgcolor: 'rgba(99,102,241,0.15)', color: '#818cf8', fontWeight: 600, fontSize: 11 }} />
                    <Chip label={`${headers.length} columns`} size="small" sx={{ bgcolor: 'rgba(139,92,246,0.15)', color: '#a78bfa', fontWeight: 600, fontSize: 11 }} />
                </Box>
            </Box>
        );
    }

    // Plain text with line numbers
    const lines = content.split('\n');
    return (
        <Box sx={{ height: '100%', overflow: 'auto', p: 2.5 }}>
            <Box sx={{
                borderRadius: 3, overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                bgcolor: 'rgba(0,0,0,0.3)',
                fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
                fontSize: '0.85rem', lineHeight: 1.7
            }}>
                {lines.map((line, i) => (
                    <Box key={i} sx={{
                        display: 'flex', px: 0,
                        '&:hover': { bgcolor: 'rgba(99,102,241,0.06)' },
                        transition: 'background 0.15s'
                    }}>
                        <Box sx={{
                            minWidth: 55, textAlign: 'right', pr: 2, pl: 1.5, py: 0.3,
                            color: 'rgba(255,255,255,0.2)', userSelect: 'none', fontSize: '0.8rem',
                            borderRight: '1px solid rgba(255,255,255,0.06)', flexShrink: 0
                        }}>{i + 1}</Box>
                        <Box sx={{ pl: 2, py: 0.3, color: 'rgba(255,255,255,0.85)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', flexGrow: 1 }}>
                            {line || '\u00A0'}
                        </Box>
                    </Box>
                ))}
            </Box>
            <Box sx={{ mt: 2 }}>
                <Chip label={`${lines.length} lines`} size="small" sx={{ bgcolor: 'rgba(99,102,241,0.15)', color: '#818cf8', fontWeight: 600, fontSize: 11 }} />
            </Box>
        </Box>
    );
};

/* ─────── Office Document Viewer (Google Docs Viewer + Office Online fallback) ─────── */
const OfficeFilePreview = ({ fileData, onDownload }) => {
    const [viewUrl, setViewUrl] = React.useState(null);
    const [signedUrl, setSignedUrl] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [viewerType, setViewerType] = React.useState('google'); // 'google' or 'office'
    const [iframeError, setIframeError] = React.useState(false);

    const buildViewerUrl = React.useCallback((rawUrl, viewer) => {
        if (viewer === 'google') {
            return `https://docs.google.com/gview?url=${encodeURIComponent(rawUrl)}&embedded=true`;
        }
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(rawUrl)}`;
    }, []);

    React.useEffect(() => {
        if (!fileData?.docId) { setError('No document ID'); setLoading(false); return; }
        setLoading(true);
        setError(null);
        setIframeError(false);

        import('../services/api').then(({ default: api }) => {
            const versionPath = fileData.versionNumber ? `/${fileData.versionNumber}` : '';
            api.get(`/documents/${fileData.docId}/view-url${versionPath}`)
                .then(res => {
                    const url = res.data.url;
                    setSignedUrl(url);
                    setViewUrl(buildViewerUrl(url, 'google'));
                    setViewerType('google');
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Failed to get view URL:', err);
                    setError('Could not load preview');
                    setLoading(false);
                });
        });
    }, [fileData?.docId, fileData?.versionNumber, buildViewerUrl]);

    const switchViewer = () => {
        if (!signedUrl) return;
        const next = viewerType === 'google' ? 'office' : 'google';
        setViewerType(next);
        setViewUrl(buildViewerUrl(signedUrl, next));
        setIframeError(false);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <CircularProgress sx={{ color: '#6366f1' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', letterSpacing: 2, fontWeight: 600, textTransform: 'uppercase' }}>
                    Loading document viewer...
                </Typography>
            </Box>
        );
    }

    if (error || !viewUrl) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 3, p: 4 }}>
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(139,92,246,0.2))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DownloadIcon sx={{ fontSize: 40, color: '#818cf8' }} />
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
                    {error || 'Preview unavailable'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', maxWidth: 400 }}>
                    Download the file to view it in Microsoft Office or another compatible application.
                </Typography>
                <Button
                    variant="contained" startIcon={<DownloadIcon />}
                    onClick={() => onDownload(fileData)}
                    sx={{
                        borderRadius: 3, px: 4, py: 1.5,
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        boxShadow: '0 8px 20px rgba(99,102,241,0.3)',
                        '&:hover': { transform: 'translateY(-2px)' }, transition: 'all 0.2s'
                    }}
                >Download File</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            {/* Viewer toggle bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 0.5, px: 2, bgcolor: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, mr: 1 }}>
                    Viewer: {viewerType === 'google' ? 'Google Docs' : 'Microsoft Office'}
                </Typography>
                <Button size="small" onClick={switchViewer}
                    sx={{ color: '#818cf8', fontSize: 11, textTransform: 'none', minWidth: 'auto', '&:hover': { bgcolor: 'rgba(99,102,241,0.1)' } }}>
                    Switch to {viewerType === 'google' ? 'Office Online' : 'Google Docs'}
                </Button>
                <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: '14px !important' }} />} onClick={() => onDownload(fileData)}
                    sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, textTransform: 'none', ml: 'auto', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                    Download
                </Button>
            </Box>
            <iframe
                src={viewUrl}
                width="100%"
                style={{ border: 'none', flexGrow: 1, background: '#fff', minHeight: 0 }}
                title={fileData.title}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                onError={() => setIframeError(true)}
            />
            {iframeError && (
                <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', bgcolor: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 2, px: 2, py: 1 }}>
                    <Typography sx={{ color: '#f87171', fontSize: 12 }}>
                        Viewer failed to load. Try switching viewer or downloading the file.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

/* ─────── File type icon + color helper ─────── */
const getFileTypeInfo = (fileData) => {
    if (!fileData) return { icon: <TextIcon />, color: '#64748b', label: 'FILE', gradient: 'linear-gradient(135deg, #475569, #64748b)' };
    const isPdf = fileData.isPdf || fileData.mimeType === 'application/pdf';
    const isImage = fileData.isImage || fileData.mimeType?.startsWith('image/');
    const isCsv = fileData.mimeType === 'text/csv' || fileData.mimeType === 'application/csv' || fileData.title?.toLowerCase().endsWith('.csv');
    const isOffice = fileData.mimeType?.includes('word') || fileData.mimeType?.includes('spreadsheet') ||
        fileData.mimeType?.includes('presentation') || fileData.mimeType?.includes('officedocument') ||
        fileData.mimeType?.includes('msword') || fileData.mimeType?.includes('ms-excel') ||
        fileData.mimeType?.includes('ms-powerpoint') || fileData.mimeType === 'application/rtf' ||
        /\.(docx?|xlsx?|pptx?|rtf)$/i.test(fileData.title || '');
    if (isPdf) return { icon: <PdfIcon />, color: '#ef4444', label: 'PDF', gradient: 'linear-gradient(135deg, #dc2626, #ef4444)' };
    if (isImage) return { icon: <ImageIcon />, color: '#06b6d4', label: 'IMAGE', gradient: 'linear-gradient(135deg, #0891b2, #06b6d4)' };
    if (isOffice) {
        const title = (fileData.title || '').toLowerCase();
        if (fileData.mimeType?.includes('spreadsheet') || fileData.mimeType?.includes('excel') || title.endsWith('.xlsx') || title.endsWith('.xls'))
            return { icon: <CsvIcon />, color: '#22c55e', label: 'EXCEL', gradient: 'linear-gradient(135deg, #16a34a, #22c55e)' };
        if (fileData.mimeType?.includes('presentation') || fileData.mimeType?.includes('powerpoint') || title.endsWith('.pptx') || title.endsWith('.ppt'))
            return { icon: <TextIcon />, color: '#f97316', label: 'PPTX', gradient: 'linear-gradient(135deg, #ea580c, #f97316)' };
        return { icon: <TextIcon />, color: '#3b82f6', label: 'WORD', gradient: 'linear-gradient(135deg, #2563eb, #3b82f6)' };
    }
    if (isCsv) return { icon: <CsvIcon />, color: '#22c55e', label: 'CSV', gradient: 'linear-gradient(135deg, #16a34a, #22c55e)' };
    return { icon: <TextIcon />, color: '#f59e0b', label: 'TEXT', gradient: 'linear-gradient(135deg, #d97706, #f59e0b)' };
};

/* ─────── Main Preview Dialog ─────── */
const FilePreviewDialog = ({
    open,
    onClose,
    fileData,
    onDownload,
    loading = false
}) => {
    const [zoom, setZoom] = React.useState(1);
    const [rotation, setRotation] = React.useState(0);
    const [pdfPage, setPdfPage] = React.useState(1);

    const handleReset = () => { setZoom(1); setRotation(0); };
    const handleZoomIn = () => setZoom(p => Math.min(p + 0.25, 4));
    const handleZoomOut = () => setZoom(p => Math.max(p - 0.25, 0.3));
    const handleRotate = () => setRotation(p => (p + 90) % 360);

    React.useEffect(() => {
        if (open) { handleReset(); setPdfPage(1); }
    }, [open, fileData?.url]);

    // Keyboard shortcuts
    React.useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === '+' || e.key === '=') handleZoomIn();
            if (e.key === '-') handleZoomOut();
            if (e.key === 'r') handleRotate();
            if (e.key === '0') handleReset();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open]);

    const typeInfo = getFileTypeInfo(fileData);
    const isPdf = fileData?.isPdf || fileData?.mimeType === 'application/pdf';
    const isImage = fileData?.isImage || fileData?.mimeType?.startsWith('image/');
    const isText = fileData?.mimeType?.startsWith('text/') || fileData?.mimeType === 'application/csv';
    const isCsv = fileData?.mimeType === 'text/csv' || fileData?.mimeType === 'application/csv' ||
        fileData?.title?.toLowerCase().endsWith('.csv') || fileData?.title?.toLowerCase().endsWith('.tsv');
    const isOffice = fileData?.mimeType?.includes('word') || fileData?.mimeType?.includes('spreadsheet') ||
        fileData?.mimeType?.includes('presentation') || fileData?.mimeType?.includes('officedocument') ||
        fileData?.mimeType?.includes('msword') || fileData?.mimeType?.includes('ms-excel') ||
        fileData?.mimeType?.includes('ms-powerpoint') || fileData?.mimeType === 'application/rtf' ||
        /\.(docx?|xlsx?|pptx?|rtf)$/i.test(fileData?.title || '');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            TransitionComponent={Fade}
            transitionDuration={300}
            PaperProps={{
                sx: {
                    width: '94vw', height: '92vh', maxWidth: '94vw',
                    borderRadius: 4, overflow: 'hidden',
                    bgcolor: '#0a0e1a',
                    border: '1px solid rgba(255,255,255,0.06)',
                    boxShadow: '0 40px 80px rgba(0,0,0,0.6), 0 0 120px rgba(99,102,241,0.08)',
                    display: 'flex', flexDirection: 'column'
                }
            }}
            sx={{
                '& .MuiBackdrop-root': {
                    bgcolor: 'rgba(0,0,0,0.85)',
                    backdropFilter: 'blur(12px)'
                }
            }}
        >
            {/* ─── Header Bar ─── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2.5, py: 1.5, minHeight: 56,
                background: 'linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.95) 100%)',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(20px)'
            }}>
                {/* Left: File info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: typeInfo.gradient, boxShadow: `0 4px 12px ${typeInfo.color}40`,
                        '& svg': { fontSize: 20, color: 'white' }
                    }}>
                        {typeInfo.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{
                            color: 'white', fontWeight: 700, fontSize: '0.9rem',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400
                        }}>
                            {fileData?.title || 'Document Preview'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.2 }}>
                            <Chip label={typeInfo.label} size="small" sx={{
                                height: 18, fontSize: 9, fontWeight: 800, letterSpacing: 1,
                                bgcolor: `${typeInfo.color}20`, color: typeInfo.color,
                                borderRadius: 1
                            }} />
                            {fileData?.mimeType && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem' }}>
                                    {fileData.mimeType}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Center: Toolbar */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2.5, px: 1, py: 0.5,
                    border: '1px solid rgba(255,255,255,0.06)'
                }}>
                    {(isImage || isPdf) && (
                        <>
                            <ToolbarButton icon={<ZoomOutIcon />} tooltip="Zoom Out (−)" onClick={handleZoomOut} />
                            <Box sx={{
                                px: 1.5, py: 0.5, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.06)',
                                color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', fontWeight: 700, fontFamily: 'monospace',
                                minWidth: 50, textAlign: 'center'
                            }}>
                                {Math.round(zoom * 100)}%
                            </Box>
                            <ToolbarButton icon={<ZoomInIcon />} tooltip="Zoom In (+)" onClick={handleZoomIn} />
                            <ToolbarDivider />
                        </>
                    )}
                    {isImage && (
                        <>
                            <ToolbarButton icon={<RotateIcon />} tooltip="Rotate (R)" onClick={handleRotate} />
                            <ToolbarDivider />
                        </>
                    )}
                    {(isImage || isPdf) && (
                        <ToolbarButton icon={<FitScreenIcon />} tooltip="Reset (0)" onClick={handleReset} />
                    )}
                    <ToolbarDivider />
                    <ToolbarButton icon={<DownloadIcon />} tooltip="Download" onClick={() => onDownload(fileData)} active />
                </Box>

                {/* Right: Close */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'rgba(255,255,255,0.5)', width: 36, height: 36,
                            bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2,
                            border: '1px solid rgba(255,255,255,0.06)',
                            '&:hover': { bgcolor: 'rgba(239,68,68,0.15)', color: '#f87171', borderColor: 'rgba(239,68,68,0.3)' },
                            transition: 'all 0.2s'
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* ─── Content Area ─── */}
            <Box sx={{
                flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                background: isPdf ? '#2a2a2a' : 'radial-gradient(ellipse at center, #111827 0%, #0a0e1a 70%)',
                position: 'relative'
            }}>
                {/* Ambient glow */}
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: '60%', height: '60%', borderRadius: '50%',
                    background: `radial-gradient(circle, ${typeInfo.color}08 0%, transparent 70%)`,
                    pointerEvents: 'none', zIndex: 0
                }} />

                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 3, zIndex: 1 }}>
                        <Box sx={{ position: 'relative' }}>
                            <CircularProgress size={60} thickness={2} sx={{ color: typeInfo.color }} />
                            <Box sx={{
                                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                                '& svg': { fontSize: 24, color: typeInfo.color }
                            }}>{typeInfo.icon}</Box>
                        </Box>
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', letterSpacing: 2, fontWeight: 600, textTransform: 'uppercase' }}>
                            Loading document...
                        </Typography>
                    </Box>
                ) : fileData?.url ? (
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 1 }}>
                        {isPdf && (
                            <Box sx={{
                                flexGrow: 1, overflow: 'auto', display: 'flex', justifyContent: 'center',
                                '&::-webkit-scrollbar': { width: 8 },
                                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 4 },
                                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' }
                            }}>
                                <iframe
                                    src={`${fileData.url}#toolbar=1&navpanes=1&scrollbar=1&zoom=${Math.round(zoom * 100)}`}
                                    width="100%"
                                    height="100%"
                                    title={fileData.title}
                                    style={{
                                        border: 'none', flexGrow: 1, maxWidth: 1200,
                                        borderRadius: 0, background: '#525659'
                                    }}
                                />
                            </Box>
                        )}

                        {isImage && (
                            <Box sx={{
                                flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                overflow: 'auto', p: 4, cursor: zoom > 1 ? 'grab' : 'default',
                                '&:active': { cursor: zoom > 1 ? 'grabbing' : 'default' }
                            }}>
                                <Box sx={{
                                    position: 'relative', borderRadius: 3, overflow: 'hidden',
                                    boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 40px rgba(0,0,0,0.3)',
                                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s',
                                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                                    '&:hover': { boxShadow: '0 30px 70px rgba(0,0,0,0.6), 0 0 50px rgba(0,0,0,0.4)' }
                                }}>
                                    {/* Checkerboard background for transparent images */}
                                    <Box sx={{
                                        position: 'absolute', inset: 0,
                                        backgroundImage: 'linear-gradient(45deg, #1a1a2e 25%, transparent 25%), linear-gradient(-45deg, #1a1a2e 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1a1a2e 75%), linear-gradient(-45deg, transparent 75%, #1a1a2e 75%)',
                                        backgroundSize: '20px 20px',
                                        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                                        opacity: 0.5
                                    }} />
                                    <img
                                        src={fileData.url}
                                        alt={fileData.title}
                                        style={{
                                            maxWidth: '100%', maxHeight: 'calc(92vh - 140px)',
                                            objectFit: 'contain', display: 'block',
                                            position: 'relative', zIndex: 1
                                        }}
                                        onDoubleClick={() => setZoom(z => z === 1 ? 2 : 1)}
                                    />
                                </Box>
                            </Box>
                        )}

                        {(isText || isCsv) && !isPdf && !isImage && !isOffice && (
                            <TextFilePreview url={fileData.url} isCsv={isCsv} />
                        )}

                        {isOffice && !isPdf && !isImage && (
                            <OfficeFilePreview fileData={fileData} onDownload={onDownload} />
                        )}

                        {!isPdf && !isImage && !isText && !isCsv && !isOffice && (
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                                <Box sx={{
                                    width: 100, height: 100, borderRadius: '50%',
                                    background: typeInfo.gradient, opacity: 0.3,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    '& svg': { fontSize: 50, color: 'white' }
                                }}>{typeInfo.icon}</Box>
                                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>Preview not available</Typography>
                                <Button
                                    variant="contained" startIcon={<DownloadIcon />}
                                    onClick={() => onDownload(fileData)}
                                    sx={{
                                        borderRadius: 3, px: 4, py: 1.5,
                                        background: typeInfo.gradient,
                                        boxShadow: `0 8px 20px ${typeInfo.color}30`,
                                        '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 24px ${typeInfo.color}40` },
                                        transition: 'all 0.2s'
                                    }}
                                >Download File</Button>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.3)' }}>No file selected</Typography>
                    </Box>
                )}
            </Box>

            {/* ─── Footer ─── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2.5, py: 1, minHeight: 44,
                bgcolor: 'rgba(15,23,42,0.98)',
                borderTop: '1px solid rgba(255,255,255,0.06)'
            }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    {isImage && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem' }}>
                            Double-click to zoom | Scroll to pan | Press R to rotate
                        </Typography>
                    )}
                    {isPdf && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem' }}>
                            Use PDF toolbar to navigate pages
                        </Typography>
                    )}
                </Box>
                <Button
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600,
                        borderRadius: 2, px: 2,
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', color: 'white' }
                    }}
                >
                    Close Preview
                </Button>
            </Box>
        </Dialog>
    );
};

/* ─── Toolbar sub-components ─── */
const ToolbarButton = ({ icon, tooltip, onClick, active }) => (
    <Tooltip title={tooltip} arrow>
        <IconButton
            size="small"
            onClick={onClick}
            sx={{
                width: 32, height: 32, borderRadius: 1.5,
                color: active ? '#818cf8' : 'rgba(255,255,255,0.5)',
                bgcolor: active ? 'rgba(99,102,241,0.15)' : 'transparent',
                '&:hover': { bgcolor: active ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.08)', color: 'white' },
                transition: 'all 0.15s',
                '& svg': { fontSize: 18 }
            }}
        >{icon}</IconButton>
    </Tooltip>
);

const ToolbarDivider = () => (
    <Box sx={{ width: '1px', height: 20, bgcolor: 'rgba(255,255,255,0.08)', mx: 0.5 }} />
);

export default FilePreviewDialog;
